import { useMemo } from 'react';
import {
    EntityCRUD, 
    EntityMode, 
    UIFeedbackHooks, 
    useDeleteDomain,  
    UserActions, 
    useRestoreDomain ,
    useAppNavigation
} from '@/Shared';

import { DeleteAction, DomainType, Entity, ExportAction, GenerateAction, RestoreAction } from '@/Shared/Types';

import { AccessRightModel } from '@/Domains/Auth';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import { useAppSelector } from '@/OneLiners';
import { ExportHook, useExportDocument } from '@/Domains/Document';
import { useGenerateDomain } from './useGenerateDomain';

interface UseDomainActionParams<T> {
    domainType: DomainType;
    curEntityMode: EntityMode;
    toggleEntityMode: () => void;
    deleteAction : DeleteAction<T>,
    restoreAction : RestoreAction<T>,
    generateAction : GenerateAction<T>,
    exportAction : ExportAction<T>,
    uiFeedbackHooks: UIFeedbackHooks,
    accessRight: AccessRightModel,
}

export const useDomainActions = <VM, T extends Entity>({
    domainType,
    curEntityMode,
    toggleEntityMode,
    deleteAction,
    restoreAction,
    generateAction,
    exportAction,
    uiFeedbackHooks,
    accessRight
}: UseDomainActionParams<T>): {
    userActions: UserActions<VM>,
    isDisplaying: (entityId : number, queryId?: number | string) => boolean,
    exportHook: ExportHook,
    activeDomainDetail ? : DomainType
} => {

    const { goToDetail, goToForm, goToLayout, goToSelector } = useAppNavigation();

    const exportHook = useExportDocument(domainType, uiFeedbackHooks, generateAction, exportAction);

    const activeDomainDetail  = useAppSelector(state => state.uiState.activeDomainDetail ) ?? undefined;
    
    const {domainLabel, DomainFormSelector} = DOMAIN_CONFIGS[domainType];

    const isActiveMode = curEntityMode === 'active';

    /**
         * Delete workflow delegated to a generic hook
         * Encapsulates confirmation, toast feedback, and API call
         */
    const { executeDelete } =  useDeleteDomain<T>({
        domainLabel,
        canDelete: accessRight?.canDelete?.(domainType),
        canRestore: accessRight?.canRestore?.(domainType),
        deleteAction,
        uiFeedbackHooks,
        onSuccess: () => goToLayout(domainType)
    });

    /**
     * Delete workflow delegated to a generic hook
     * Encapsulates confirmation, toast feedback, and API call
     */
    const { executeRestore } = useRestoreDomain({
        domainLabel,
        canRestore: accessRight?.canRestore?.(domainType),
        restoreAction,
        uiFeedbackHooks,
        onSuccess: () => goToLayout(domainType)
    });

    const { executeGenerate } = useGenerateDomain({
        domainType,
        canGenerate: accessRight?.canExport?.(domainType),
        generateAction,
        uiFeedbackHooks
    });


    const entityCRUD = useMemo(() => {

        const crud = {} as EntityCRUD<VM>;
        if(!accessRight) return crud;

        if (accessRight.canRead?.(domainType)) {
            crud.read = (entityId: number) => goToDetail(domainType, entityId);
        }

        if (accessRight.canWrite?.(domainType)) {

             if (!DOMAIN_CONFIGS[domainType].prohibitCreate) { 
                crud.create = () =>  DomainFormSelector ? goToSelector(domainType) : goToForm(domainType);
             }

            if (isActiveMode) {
                crud.edit = (entityId: number) => goToForm(domainType, entityId);
            }
        }

        if (!DOMAIN_CONFIGS[domainType].desableDelete) { // Can't delete nor restore a permission

            if (isActiveMode) {
                if (accessRight.canDelete?.(domainType)) {
                    crud.delete = executeDelete; //async (entity: T) => await deleteEntity?.(entity.id);
                }
                if (accessRight.canExport?.(domainType) && exportHook) {
                    crud.generate =  executeGenerate;
                }
            } else if (!isActiveMode && accessRight.canRestore?.(domainType)) {
                crud.restore = (entityId: number) => executeRestore(entityId);//async (entity: T) => await restoreEntity?.(entity.id);
            }
        }

        return crud;
    }, [
        domainType,
        isActiveMode,
        deleteAction,
        restoreAction,
        exportHook,
        accessRight,
        goToDetail,
        goToForm
    ]);

    const isDisplaying = (activityId : number, queryId? :string | number) => {
        return activityId === Number(queryId)
            && domainType === activeDomainDetail ;
    }

    return {
        exportHook,
        userActions: {
            domain: domainType,
            crud: entityCRUD,
            domainMode: {
                current: curEntityMode,
                toggle: toggleEntityMode
            }
        },
        isDisplaying,
        activeDomainDetail 
    }
};

